import { 
  FamilyTree, 
  Individual, 
  FamilyUnit, 
  ChildRelationship,
  EmotionalRelationship
} from '../models/types';

// API base URL - use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://fixedfamily-api.onrender.com';

// API client for family tree data
export const ApiClient = {
  // Family Tree
  async getFamilyTree(): Promise<FamilyTree> {
    const response = await fetch(`${API_BASE_URL}/family-tree`);
    if (!response.ok) {
      throw new Error(`Failed to fetch family tree: ${response.statusText}`);
    }
    return response.json();
  },

  // Generate sample data
  async generateSampleData(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/family-tree/sample-data`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to generate sample data: ${response.statusText}`);
    }
  },

  // Individuals
  async getIndividuals(): Promise<Individual[]> {
    const response = await fetch(`${API_BASE_URL}/individuals`);
    if (!response.ok) {
      throw new Error(`Failed to fetch individuals: ${response.statusText}`);
    }
    return response.json();
  },

  async getIndividual(id: string): Promise<Individual> {
    const response = await fetch(`${API_BASE_URL}/individuals/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch individual: ${response.statusText}`);
    }
    return response.json();
  },

  async addIndividual(individual: Omit<Individual, 'id'>): Promise<Individual> {
    const response = await fetch(`${API_BASE_URL}/individuals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(individual),
    });
    if (!response.ok) {
      throw new Error(`Failed to add individual: ${response.statusText}`);
    }
    return response.json();
  },

  async updateIndividual(id: string, individual: Partial<Individual>): Promise<Individual> {
    const response = await fetch(`${API_BASE_URL}/individuals/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(individual),
    });
    if (!response.ok) {
      throw new Error(`Failed to update individual: ${response.statusText}`);
    }
    return response.json();
  },

  async deleteIndividual(id: string): Promise<boolean> {
    const response = await fetch(`${API_BASE_URL}/individuals/${id}`, {
      method: 'DELETE',
    });
    return response.status === 204;
  },

  // Family Units
  async getFamilyUnits(): Promise<FamilyUnit[]> {
    const response = await fetch(`${API_BASE_URL}/family-units`);
    if (!response.ok) {
      throw new Error(`Failed to fetch family units: ${response.statusText}`);
    }
    return response.json();
  },

  async getFamilyUnit(id: string): Promise<FamilyUnit> {
    const response = await fetch(`${API_BASE_URL}/family-units/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch family unit: ${response.statusText}`);
    }
    return response.json();
  },

  async addFamilyUnit(familyUnit: Omit<FamilyUnit, 'id'>): Promise<FamilyUnit> {
    const response = await fetch(`${API_BASE_URL}/family-units`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(familyUnit),
    });
    if (!response.ok) {
      throw new Error(`Failed to add family unit: ${response.statusText}`);
    }
    return response.json();
  },

  async updateFamilyUnit(id: string, familyUnit: Partial<FamilyUnit>): Promise<FamilyUnit> {
    const response = await fetch(`${API_BASE_URL}/family-units/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(familyUnit),
    });
    if (!response.ok) {
      throw new Error(`Failed to update family unit: ${response.statusText}`);
    }
    return response.json();
  },

  async deleteFamilyUnit(id: string): Promise<boolean> {
    const response = await fetch(`${API_BASE_URL}/family-units/${id}`, {
      method: 'DELETE',
    });
    return response.status === 204;
  },

  // Child Relationships
  async getChildRelationships(): Promise<ChildRelationship[]> {
    const response = await fetch(`${API_BASE_URL}/child-relationships`);
    if (!response.ok) {
      throw new Error(`Failed to fetch child relationships: ${response.statusText}`);
    }
    return response.json();
  },

  async addChildRelationship(relationship: Omit<ChildRelationship, 'id'>): Promise<ChildRelationship> {
    const response = await fetch(`${API_BASE_URL}/child-relationships`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(relationship),
    });
    if (!response.ok) {
      throw new Error(`Failed to add child relationship: ${response.statusText}`);
    }
    return response.json();
  },

  // Emotional Relationships
  async getEmotionalRelationships(): Promise<EmotionalRelationship[]> {
    const response = await fetch(`${API_BASE_URL}/emotional-relationships`);
    if (!response.ok) {
      throw new Error(`Failed to fetch emotional relationships: ${response.statusText}`);
    }
    return response.json();
  },

  async getIndividualEmotionalRelationships(individualId: string): Promise<EmotionalRelationship[]> {
    const response = await fetch(`${API_BASE_URL}/emotional-relationships/individual/${individualId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch emotional relationships: ${response.statusText}`);
    }
    return response.json();
  },

  async addEmotionalRelationship(relationship: Omit<EmotionalRelationship, 'id'>): Promise<EmotionalRelationship> {
    const response = await fetch(`${API_BASE_URL}/emotional-relationships`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(relationship),
    });
    if (!response.ok) {
      throw new Error(`Failed to add emotional relationship: ${response.statusText}`);
    }
    return response.json();
  }
}; 